import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  CheckCircle, XCircle, Clock, Eye, FileText, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CONTENT_TYPE_LABELS = {
  news_event: 'News & Event',
  variety: 'Variety',
  technology: 'Technology',
  job: 'Job',
  tender: 'Tender',
  banner: 'Banner',
  announcement: 'Announcement',
  research: 'Research',
  useful_link: 'Useful Link',
  staff: 'Staff Member',
};

export default function ApprovalQueuePage() {
  const { token, user, isAdmin, isSuperAdmin } = useAuth();
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [remark, setRemark] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isAdmin || isSuperAdmin) {
      fetchPendingApprovals();
    }
  }, [isAdmin, isSuperAdmin]);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/workflow/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      toast.error('Failed to fetch pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedItem) return;

    try {
      setProcessing(true);
      await axios.post(
        `${API_URL}/api/workflow/approve`,
        {
          content_id: selectedItem.id,
          content_type: selectedItem.content_type,
          remark: remark || null,
          auto_publish: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Content approved and published successfully!');
      setShowApproveModal(false);
      setSelectedItem(null);
      setRemark('');
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error approving content:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve content');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedItem) return;

    if (!remark || remark.trim() === '') {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessing(true);
      await axios.post(
        `${API_URL}/api/workflow/reject`,
        {
          content_id: selectedItem.id,
          content_type: selectedItem.content_type,
          remark: remark
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Content rejected successfully');
      setShowRejectModal(false);
      setSelectedItem(null);
      setRemark('');
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error rejecting content:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject content');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (!isAdmin && !isSuperAdmin) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">Only Admins can access the Approval Queue.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            Approval Queue
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Review and approve content submitted by creators
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-3"></div>
            <p className="text-gray-500">Loading pending approvals...</p>
          </div>
        ) : pendingItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">There are no items pending approval at the moment.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Title/Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Submitted By</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Submitted At</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pendingItems.map((item) => (
                  <tr key={`${item.content_type}-${item.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                        {CONTENT_TYPE_LABELS[item.content_type] || item.content_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">
                        {item.title || item.name || item.variety_name || item.full_name || 'Untitled'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {item.workflow_created_by_name || item.workflow_submitted_by_name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(item.workflow_submitted_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedItem(item);
                            setRemark('');
                            setShowApproveModal(true);
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedItem(item);
                            setRemark('');
                            setShowRejectModal(true);
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Approve Modal */}
        <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Content</DialogTitle>
              <DialogDescription>
                Are you sure you want to approve and publish this content?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remark (Optional)
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows={3}
                  placeholder="Add a comment (optional)..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveModal(false)} disabled={processing}>
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={processing}
                className="bg-green-600 hover:bg-green-700"
              >
                {processing ? 'Approving...' : 'Approve & Publish'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Content</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this content.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Rejection *
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={4}
                  placeholder="Explain why this content is being rejected..."
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectModal(false)} disabled={processing}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={processing || !remark.trim()}
              >
                {processing ? 'Rejecting...' : 'Reject Content'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
